import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Minus, Trash2, Repeat, Check, FileDown, FileText, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp } from "@/lib/payroll";
import { useT, translateJabatan } from "@/i18n";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import NumField from "@/components/NumField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";

const API = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const now = new Date();
const FIELD = "focus-ring h-11 w-full rounded-xl border border-white/70 bg-white/70 px-3.5 text-[15px] backdrop-blur-sm placeholder:text-[#B0B0B8]";
const DATE_FIELD = `${FIELD} appearance-none text-left [&::-webkit-date-and-time-value]:text-left [&::-webkit-date-and-time-value]:text-[15px] [&::-webkit-calendar-picker-indicator]:ml-auto`;
const GLASS = "max-h-[90vh] overflow-y-auto rounded-[26px] border border-white/60 bg-gradient-to-br from-white/80 to-white/50 shadow-[0_24px_80px_rgba(0,0,0,0.22)] ring-1 ring-white/50 backdrop-blur-2xl";
const fmtTgl = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  const loc = { id: "id-ID", en: "en-US", ko: "ko-KR", ja: "ja-JP" }[lang] || "id-ID";
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(loc, { day: "numeric", month: "short", year: "numeric" });
};

const detectCat = (s) => (/(kasbon|uang)/i.test(s || "") ? "uang" : "barang");

const CatToggle = ({ value, onPick, t }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="kasbon-cat-toggle">
    {["uang", "barang"].map((c) => (
      <button key={c} type="button" data-testid={`kasbon-cat-${c}`} onClick={() => onPick(c)}
        className={`h-11 rounded-xl border text-[13px] font-semibold transition-all active:scale-95 ${value === c ? (c === "uang" ? "border-[#0071E3] bg-[#EAF2FE] text-[#0071E3]" : "border-[#C0362C] bg-[#FDECEC] text-[#C0362C]") : "border-[#E1E1E6] bg-white text-[#8E8E93]"}`}>
        {c === "uang" ? t("kasbon.bonUang") : t("kasbon.bonBarang")}
      </button>
    ))}
  </div>
);

const CatBadge = ({ cat, t }) => (
  <span className={`ml-1.5 inline-block align-middle rounded-full px-1.5 py-[1px] text-[9.5px] font-semibold ${cat === "uang" ? "bg-[#EAF2FE] text-[#0071E3]" : "bg-[#FDECEC] text-[#C0362C]"}`}>
    {cat === "uang" ? t("kasbon.bonUang") : t("kasbon.bonBarang")}
  </span>
);

export default function KasbonEmployee() {
  const { empId } = useParams();
  const navigate = useNavigate();
  const { readOnly } = useAuth();
  const { t, lang, months } = useT();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ket, setKet] = useState("");
  const [charge, setCharge] = useState(0);
  const [chargeDate, setChargeDate] = useState(today());
  const [payNom, setPayNom] = useState(0);
  const [payDate, setPayDate] = useState(today());
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [cicilan, setCicilan] = useState(0);
  const [savingCic, setSavingCic] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [chargeCat, setChargeCat] = useState("uang");
  const [chargeCatTouched, setChargeCatTouched] = useState(false);
  const [payCat, setPayCat] = useState("uang");
  const [edit, setEdit] = useState(null);
  const [barangRows, setBarangRows] = useState([{ qty: 1, nama: "", harga: 0 }]);

  const reload = useCallback(() => {
    api.get(`/kasbon/${empId}`).then((r) => { setData(r.data); setCicilan(r.data.employee?.cicilan || 0); }).catch((e) => toast.error(errMsg(e)));
  }, [empId]);
  useEffect(() => { reload(); }, [reload]);

  const dl = (fmt) => window.open(`${API}/api/kasbon/${empId}/${fmt}`, "_blank");

  const addCharge = async () => {
    setBusy(true);
    try {
      if (chargeCat === "barang") {
        const items = barangRows
          .map((r) => ({ qty: Number(r.qty) || 1, nama: (r.nama || "").trim(), harga: Number(r.harga) || 0 }))
          .filter((r) => r.harga > 0)
          .map((r) => ({ keterangan: r.qty > 1 ? `${r.qty}x ${r.nama}` : r.nama, nominal: r.qty * r.harga }));
        if (!items.length) { setBusy(false); return toast.error(t("kasbon.addErr")); }
        await api.post(`/kasbon/${empId}/charge/bulk`, { tanggal: chargeDate, kategori: "barang", items });
      } else {
        if (!charge || charge <= 0) { setBusy(false); return toast.error(t("kasbon.addErr")); }
        await api.post(`/kasbon/${empId}/charge`, { keterangan: ket, nominal: Number(charge), tanggal: chargeDate, kategori: "uang" });
      }
      toast.success(t("kasbon.addToast"));
      setKet(""); setCharge(0); setBarangRows([{ qty: 1, nama: "", harga: 0 }]);
      setChargeCat("uang"); setChargeCatTouched(false); setChargeOpen(false); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const addPayment = async () => {
    if (!payNom || payNom <= 0) return toast.error(t("kasbon.payErr"));
    setBusy(true);
    try {
      await api.post(`/kasbon/${empId}/payment`, { nominal: Number(payNom), bulan, tahun, tanggal: payDate, kategori: payCat });
      toast.success(t("kasbon.payToast", { label: `${months[bulan - 1]} ${tahun}` }));
      setPayNom(0); setPayCat("uang"); setPayOpen(false); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveCicilan = async () => {
    setSavingCic(true);
    try {
      await api.put(`/kasbon/${empId}/cicilan`, { nominal: Number(cicilan) || 0 });
      toast.success(t("kasbon.cicilanSaved"));
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingCic(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/kasbon/entry/${id}`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveEdit = async () => {
    if (!edit || !edit.nominal || edit.nominal <= 0) return toast.error(t("kasbon.addErr"));
    setBusy(true);
    try {
      await api.put(`/kasbon/entry/${edit.id}`, {
        keterangan: edit.keterangan, nominal: Number(edit.nominal), tanggal: edit.tanggal,
        kategori: edit.kategori, bulan: edit.bulan, tahun: edit.tahun,
      });
      toast.success(t("kasbon.editToast"));
      setEdit(null); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const openEdit = (l) => setEdit({
    id: l.id, kind: l.kind, keterangan: l.keterangan || "", nominal: l.nominal || 0,
    kategori: l.kategori || "uang", tanggal: l.tanggal || today(),
    bulan: l.bulan || bulan, tahun: l.tahun || tahun,
  });

  const emp = data?.employee;
  const payCatBal = data?.cat ? Math.max(0, payCat === "uang" ? data.cat.uang.balance : data.cat.barang.balance) : Math.max(0, data?.balance || 0);

  return (
    <div data-testid="kasbon-employee-page" className="w-full space-y-5">
      <button data-testid="kasbon-back" onClick={() => navigate("/kasbon")}
        className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#0071E3] hover:underline">
        <ArrowLeft className="h-4 w-4" /> {t("kasbon.backList")}
      </button>

      {!data ? (
        <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#C8C8CE]" /></div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <EmployeeAvatar id={emp.id} nama={emp.nama} hasPhoto={emp.has_photo} size={48} />
              <div className="min-w-0">
                <p className="truncate text-[20px] font-semibold tracking-tight">{emp.nama}</p>
                <p className="num truncate text-[12.5px] text-[#8E8E93]">{emp.emp_code || "\u2014"} · {translateJabatan(lang, emp.jabatan)}</p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <button data-testid="kasbon-export-xlsx" onClick={() => dl("xlsx")}
                className="flex h-9 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white px-3 text-[13px] font-medium transition-all hover:bg-[#F2F2F7] active:scale-95">
                <FileDown className="h-4 w-4 text-[#0E8A46]" /> Excel
              </button>
              <button data-testid="kasbon-export-pdf" onClick={() => dl("pdf")}
                className="flex h-9 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white px-3 text-[13px] font-medium transition-all hover:bg-[#F2F2F7] active:scale-95">
                <FileText className="h-4 w-4 text-[#C0362C]" /> PDF
              </button>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-white/60 bg-white/55 p-5 shadow-[0_10px_40px_rgba(0,0,0,0.08)] backdrop-blur-2xl ring-1 ring-black/[0.04]">
            <div className="pointer-events-none absolute -right-10 -top-12 h-36 w-36 rounded-full bg-[#C0362C]/15 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-10 -left-8 h-28 w-28 rounded-full bg-[#0071E3]/10 blur-3xl" />
            <div className="relative">
              <p className="text-[12px] font-medium text-[#8E8E93]">{t("kasbon.balance")}</p>
              <p className="num mt-0.5 text-[32px] font-semibold tracking-tight text-[#C0362C]" data-testid="kasbon-balance">Rp {rp(data.balance)}</p>
              <div className="mt-4 flex border-t border-black/[0.06] pt-3.5">
                <div className="flex-1">
                  <p className="text-[11px] text-[#8E8E93]">{t("kasbon.totalDebt")}</p>
                  <p className="num mt-0.5 text-[14px] font-semibold">Rp {rp(data.total_charge)}</p>
                </div>
                <div className="w-px bg-black/[0.06]" />
                <div className="flex-1 pl-4">
                  <p className="text-[11px] text-[#8E8E93]">{t("kasbon.paid")}</p>
                  <p className="num mt-0.5 text-[14px] font-semibold text-[#0E8A46]">Rp {rp(data.total_payment)}</p>
                </div>
              </div>
            </div>
          </div>

          {data.cat && (
            <div className="grid grid-cols-2 gap-3">
              <div className="relative overflow-hidden rounded-3xl border border-white/60 bg-white/50 p-4 shadow-[0_8px_30px_rgba(0,113,227,0.10)] backdrop-blur-2xl ring-1 ring-[#0071E3]/10" data-testid="kasbon-cat-uang">
                <div className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-[#0071E3]/20 blur-2xl" />
                <div className="relative">
                  <p className="text-[11px] font-semibold text-[#0071E3]">{t("kasbon.bonUang")}</p>
                  <p className="num mt-0.5 text-[20px] font-bold tracking-tight text-[#1D1D1F]">Rp {rp(data.cat.uang.balance)}</p>
                  <p className="num mt-1 text-[10.5px] leading-snug text-[#8E8E93]">{t("kasbon.totalDebt")} Rp {rp(data.cat.uang.charge)} · {t("kasbon.paid")} Rp {rp(data.cat.uang.payment)}</p>
                </div>
              </div>
              <div className="relative overflow-hidden rounded-3xl border border-white/60 bg-white/50 p-4 shadow-[0_8px_30px_rgba(192,54,44,0.10)] backdrop-blur-2xl ring-1 ring-[#C0362C]/10" data-testid="kasbon-cat-barang">
                <div className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-[#C0362C]/20 blur-2xl" />
                <div className="relative">
                  <p className="text-[11px] font-semibold text-[#C0362C]">{t("kasbon.bonBarang")}</p>
                  <p className="num mt-0.5 text-[20px] font-bold tracking-tight text-[#1D1D1F]">Rp {rp(data.cat.barang.balance)}</p>
                  <p className="num mt-1 text-[10.5px] leading-snug text-[#8E8E93]">{t("kasbon.totalDebt")} Rp {rp(data.cat.barang.charge)} · {t("kasbon.paid")} Rp {rp(data.cat.barang.payment)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {!readOnly && (<>
            <button data-testid="kasbon-new-charge" onClick={() => setChargeOpen(true)}
              className="flex h-14 items-center justify-center gap-2 rounded-2xl bg-[#C0362C] px-3 text-[14px] font-semibold text-white shadow-[0_8px_20px_rgba(192,54,44,0.22)] transition-all hover:bg-[#B02A20] active:scale-[0.98]">
              <Plus className="h-4.5 w-4.5 shrink-0" /> <span className="truncate">{t("kasbon.addBtn")}</span>
            </button>
            <button data-testid="kasbon-new-payment" onClick={() => setPayOpen(true)}
              className="flex h-14 items-center justify-center gap-2 rounded-2xl bg-[#0E8A46] px-3 text-[14px] font-semibold text-white shadow-[0_8px_20px_rgba(14,138,70,0.22)] transition-all hover:bg-[#0C7A3D] active:scale-[0.98]">
              <Minus className="h-4.5 w-4.5 shrink-0" /> <span className="truncate">{t("kasbon.payBtn")}</span>
            </button>
            </>)}
          </div>

          {/* Add charge (utang) dialog */}
          <Dialog open={chargeOpen} onOpenChange={setChargeOpen}>
            <DialogContent data-testid="kasbon-charge-dialog" className={`${GLASS} max-w-md`}>
              <DialogHeader className="relative text-left">
                <DialogTitle className="flex items-center gap-2 text-[17px] font-semibold tracking-tight">
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#FDECEC] text-[#C0362C]"><Plus className="h-4 w-4" /></span>
                  {t("kasbon.addTitle")}
                </DialogTitle>
              </DialogHeader>
              <div className="relative mt-2 space-y-3">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.tanggalLbl")}</label>
                  <input type="date" data-testid="kasbon-charge-date" value={chargeDate} onChange={(e) => setChargeDate(e.target.value)} className={DATE_FIELD} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.jenis")}</label>
                  <CatToggle value={chargeCat} onPick={setChargeCat} t={t} />
                </div>
                {chargeCat === "uang" ? (
                  <>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.colCharge")}</label>
                      <NumField testid="kasbon-charge-nominal" value={charge} onChange={setCharge} align="text-left" className="h-11 w-full text-[15px]" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.noteLabel")}</label>
                      <input data-testid="kasbon-charge-ket" value={ket} onChange={(e) => setKet(e.target.value)} placeholder={t("kasbon.notePlaceholder")} className={FIELD} />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2" data-testid="kasbon-barang-editor">
                    <div className="flex items-center gap-2 px-0.5 text-[10px] font-medium uppercase tracking-wide text-[#A1A1A6]">
                      <span className="w-12 shrink-0">{t("kasbon.qty")}</span>
                      <span className="flex-1">{t("kasbon.namaBarang")}</span>
                      <span className="w-24 shrink-0 text-right">{t("kasbon.hargaSatuan")}</span>
                      <span className="w-6 shrink-0" />
                    </div>
                    {barangRows.map((row, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`kasbon-barang-row-${i}`}>
                        <NumField testid={`kasbon-barang-qty-${i}`} value={row.qty} onChange={(v) => setBarangRows((rs) => rs.map((r, j) => (j === i ? { ...r, qty: v } : r)))} align="text-center" className="h-10 w-12 shrink-0 text-[14px]" />
                        <input data-testid={`kasbon-barang-nama-${i}`} value={row.nama} onChange={(e) => setBarangRows((rs) => rs.map((r, j) => (j === i ? { ...r, nama: e.target.value } : r)))} placeholder={t("kasbon.namaBarang")} className="focus-ring h-10 min-w-0 flex-1 rounded-xl border border-[#E1E1E6] bg-white px-2.5 text-[14px] placeholder:text-[#C0C0C6]" />
                        <NumField testid={`kasbon-barang-harga-${i}`} value={row.harga} onChange={(v) => setBarangRows((rs) => rs.map((r, j) => (j === i ? { ...r, harga: v } : r)))} align="text-right" className="h-10 w-24 shrink-0 text-[14px]" />
                        <button type="button" data-testid={`kasbon-barang-del-${i}`} onClick={() => setBarangRows((rs) => rs.length > 1 ? rs.filter((_, j) => j !== i) : rs)}
                          className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[#C8C8CE] transition-all hover:bg-[#FDF0F2] hover:text-[#C8102E] disabled:opacity-30" disabled={barangRows.length <= 1}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <button type="button" data-testid="kasbon-barang-add" onClick={() => setBarangRows((rs) => [...rs, { qty: 1, nama: "", harga: 0 }])}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-[#C0362C]/40 py-2 text-[13px] font-semibold text-[#C0362C] transition-all hover:bg-[#FDECEC] active:scale-[0.99]">
                      <Plus className="h-4 w-4" /> {t("kasbon.addBarang")}
                    </button>
                    <div className="flex items-center justify-between border-t border-black/[0.06] pt-2 text-[13px]">
                      <span className="font-medium text-[#6E6E73]">{t("kasbon.totalLbl")}</span>
                      <span className="num font-bold text-[#C0362C]">Rp {rp(barangRows.reduce((s, r) => s + (Number(r.qty) || 1) * (Number(r.harga) || 0), 0))}</span>
                    </div>
                  </div>
                )}
                <button data-testid="kasbon-charge-submit" onClick={addCharge} disabled={busy}
                  className="mt-1 flex h-12 w-full items-center justify-center gap-1.5 rounded-xl bg-[#C0362C] text-[15px] font-semibold text-white transition-all hover:bg-[#B02A20] active:scale-[0.99] disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("kasbon.addBtn")}
                </button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Record payment (potongan) dialog */}
          <Dialog open={payOpen} onOpenChange={setPayOpen}>
            <DialogContent data-testid="kasbon-payment-dialog" className={`${GLASS} max-w-md`}>
              <DialogHeader className="relative text-left">
                <DialogTitle className="flex items-center gap-2 text-[17px] font-semibold tracking-tight">
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#E9F9EF] text-[#0E8A46]"><Minus className="h-4 w-4" /></span>
                  {t("kasbon.payTitle")}
                </DialogTitle>
              </DialogHeader>
              <div className="relative mt-2 space-y-3">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.tanggalLbl")}</label>
                  <input type="date" data-testid="kasbon-pay-date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className={DATE_FIELD} />
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <Select value={String(bulan)} onValueChange={(v) => setBulan(+v)}>
                    <SelectTrigger data-testid="kasbon-pay-month" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[14px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl">{months.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={String(tahun)} onValueChange={(v) => setTahun(+v)}>
                    <SelectTrigger data-testid="kasbon-pay-year" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[14px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl">{[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.jenis")}</label>
                  <CatToggle value={payCat} onPick={setPayCat} t={t} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.colPayment")}</label>
                  <div className="flex items-center gap-2">
                    <NumField testid="kasbon-pay-nominal" value={payNom} onChange={setPayNom} align="text-left" className="h-11 flex-1 text-[15px]" />
                    {payCatBal > 0 && (
                      <button data-testid="kasbon-pay-fill" type="button" onClick={() => setPayNom(payCatBal)}
                        className="h-11 shrink-0 rounded-xl border border-[#0E8A46]/30 bg-[#E9F9EF] px-3.5 text-[12.5px] font-semibold text-[#0E8A46] transition-all active:scale-95">
                        {t("kasbon.lunasiBtn")}
                      </button>
                    )}
                  </div>
                </div>
                <button data-testid="kasbon-pay-submit" onClick={addPayment} disabled={busy}
                  className="mt-1 flex h-12 w-full items-center justify-center gap-1.5 rounded-xl bg-[#0E8A46] text-[15px] font-semibold text-white transition-all hover:bg-[#0C7A3D] active:scale-[0.99] disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Minus className="h-4 w-4" />} {t("kasbon.payBtn")}
                </button>
                <p className="text-[11.5px] leading-snug text-[#8E8E93]">{t("kasbon.payNote")}</p>

                {/* Monthly auto-deduction (cicilan) */}
                <div className="flex items-center gap-3 rounded-2xl bg-[#F7F8FA] p-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#EAF2FE] text-[#0071E3]"><Repeat className="h-[18px] w-[18px]" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold">{t("kasbon.cicilanTitle")}</p>
                    <p className="text-[11px] text-[#8E8E93]">{t("kasbon.cicilanDesc")}</p>
                  </div>
                  <NumField testid="kasbon-cicilan-nominal" value={cicilan} onChange={setCicilan} align="text-left" className="h-10 w-[96px] text-[14px]" />
                  {!readOnly && (
                  <button data-testid="kasbon-cicilan-save" onClick={saveCicilan} disabled={savingCic}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0071E3] text-white transition-all hover:bg-[#0077ED] active:scale-95 disabled:opacity-50">
                    {savingCic ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Ledger */}
          <div className="card-soft overflow-hidden">
            <p className="px-4 pb-2 pt-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#A1A1A6]">{t("kasbon.ledgerTitle")}</p>
            {data.entries.length === 0 ? (
              <p data-testid="kasbon-empty" className="px-4 py-8 text-center text-[13px] text-[#8E8E93]">{t("kasbon.empty")}</p>
            ) : (
              <>
              {/* Mobile card list */}
              <ul className="divide-y divide-black/[0.05] sm:hidden">
                {data.entries.map((l) => (
                  <li key={l.id} data-testid={`kasbon-m-entry-${l.id}`} className="flex items-start justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="num text-[11.5px] text-[#8E8E93]">{fmtTgl(l.tanggal, lang)}</p>
                      <p className="mt-0.5 text-[13.5px] font-medium leading-snug text-[#1D1D1F]">{l.keterangan}<CatBadge cat={l.kategori} t={t} /></p>
                      {l.created_by ? <p className="text-[10.5px] text-[#A1A1A6]">{t("kasbon.by")}: {l.created_by}</p> : null}
                    </div>
                    <div className="flex shrink-0 items-start gap-1">
                      <div className="text-right">
                        <p className={`num text-[14px] font-semibold ${l.kind === "charge" ? "text-[#C0362C]" : "text-[#0E8A46]"}`}>
                          {l.kind === "charge" ? "+" : "\u2212"}Rp {rp(l.nominal)}
                        </p>
                        <p className="num mt-0.5 text-[11px] text-[#8E8E93]">{t("kasbon.colBalance")}: <span className="font-semibold text-[#1D1D1F]">Rp {rp(l.running)}</span></p>
                      </div>
                      {!l.auto && !readOnly && (
                        <div className="flex shrink-0 flex-col items-center gap-0.5">
                  <button data-testid={`kasbon-m-edit-${l.id}`} onClick={() => openEdit(l)}
                            className="grid h-7 w-7 place-items-center rounded-full text-[#B0B0B8] transition-all hover:bg-[#EAF2FE] hover:text-[#0071E3] active:scale-90">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button data-testid={`kasbon-m-del-${l.id}`} onClick={() => setDelTarget(l)}
                            className="grid h-7 w-7 place-items-center rounded-full text-[#C8C8CE] transition-all hover:bg-[#FDF0F2] hover:text-[#C8102E] active:scale-90">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {/* Mobile totals */}
              <div data-testid="kasbon-m-totals" className="border-t-2 border-black/[0.08] bg-[#FAFAFB] px-4 py-3 sm:hidden">
                <div className="flex items-center justify-between text-[12px]"><span className="text-[#8E8E93]">{t("kasbon.totUtang")}</span><span className="num font-semibold text-[#C0362C]">Rp {rp(data.total_charge)}</span></div>
                <div className="mt-1 flex items-center justify-between text-[12px]"><span className="text-[#8E8E93]">{t("kasbon.totPotongan")}</span><span className="num font-semibold text-[#0E8A46]">Rp {rp(data.total_payment)}</span></div>
                <div className="mt-1.5 flex items-center justify-between border-t border-black/[0.06] pt-1.5 text-[13px]"><span className="font-semibold text-[#6E6E73]">{t("kasbon.saldoAkhir")}</span><span className="num font-bold text-[#1D1D1F]">Rp {rp(data.balance)}</span></div>
              </div>
              {/* Desktop table */}
              <div className="hidden overflow-x-auto sm:block">
                <table className="w-full min-w-[580px] border-collapse text-[13px]">
                  <thead>
                    <tr className="border-y border-black/[0.06] bg-[#FAFAFB] text-left text-[10.5px] uppercase tracking-wide text-[#8E8E93]">
                      <th className="px-4 py-2 font-medium">{t("kasbon.colDate")}</th>
                      <th className="px-3 py-2 font-medium">{t("kasbon.colDesc")}</th>
                      <th className="px-3 py-2 text-right font-medium">{t("kasbon.colCharge")}</th>
                      <th className="px-3 py-2 text-right font-medium">{t("kasbon.colPayment")}</th>
                      <th className="px-3 py-2 text-right font-medium">{t("kasbon.colBalance")}</th>
                      <th className="px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/[0.05]">
                    {data.entries.map((l) => (
                      <tr key={l.id} data-testid={`kasbon-entry-${l.id}`} className="group transition-colors hover:bg-black/[0.015]">
                        <td className="num whitespace-nowrap px-4 py-2.5 text-[12.5px] text-[#48484A]">{fmtTgl(l.tanggal, lang)}</td>
                        <td className="px-3 py-2.5">
                          <p className="text-[13px] font-medium text-[#1D1D1F]">{l.keterangan}<CatBadge cat={l.kategori} t={t} /></p>
                          {l.created_by ? <p className="text-[10.5px] text-[#A1A1A6]">{t("kasbon.by")}: {l.created_by}</p> : null}
                        </td>
                        <td className="num whitespace-nowrap px-3 py-2.5 text-right text-[13px] font-semibold text-[#C0362C]">{l.kind === "charge" ? `Rp ${rp(l.nominal)}` : "\u2014"}</td>
                        <td className="num whitespace-nowrap px-3 py-2.5 text-right text-[13px] font-semibold text-[#0E8A46]">{l.kind === "payment" ? `Rp ${rp(l.nominal)}` : "\u2014"}</td>
                        <td className="num whitespace-nowrap px-3 py-2.5 text-right text-[13px] font-bold text-[#1D1D1F]">Rp {rp(l.running)}</td>
                        <td className="px-2 py-2.5 text-right">
                          {!l.auto && !readOnly && (
                          <div className="flex items-center justify-end gap-0.5">
                            <button data-testid={`kasbon-edit-${l.id}`} onClick={() => openEdit(l)}
                              className="grid h-7 w-7 place-items-center rounded-full text-[#B0B0B8] opacity-100 transition-all hover:bg-[#EAF2FE] hover:text-[#0071E3] sm:opacity-0 sm:group-hover:opacity-100">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button data-testid={`kasbon-del-${l.id}`} onClick={() => setDelTarget(l)}
                              className="grid h-7 w-7 place-items-center rounded-full text-[#C8C8CE] opacity-100 transition-all hover:bg-[#FDF0F2] hover:text-[#C8102E] sm:opacity-0 sm:group-hover:opacity-100">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr data-testid="kasbon-ledger-totals" className="border-t-2 border-black/[0.08] bg-[#FAFAFB] text-[12.5px]">
                      <td className="px-4 py-3 font-semibold text-[#6E6E73]" colSpan={2}>{t("kasbon.saldoAkhir")}</td>
                      <td className="num whitespace-nowrap px-3 py-3 text-right font-semibold text-[#C0362C]">
                        <span className="block text-[9.5px] font-medium uppercase tracking-wide text-[#A1A1A6]">{t("kasbon.totUtang")}</span>
                        Rp {rp(data.total_charge)}
                      </td>
                      <td className="num whitespace-nowrap px-3 py-3 text-right font-semibold text-[#0E8A46]">
                        <span className="block text-[9.5px] font-medium uppercase tracking-wide text-[#A1A1A6]">{t("kasbon.totPotongan")}</span>
                        Rp {rp(data.total_payment)}
                      </td>
                      <td className="num whitespace-nowrap px-3 py-3 text-right text-[14px] font-bold text-[#1D1D1F]">
                        <span className="block text-[9.5px] font-medium uppercase tracking-wide text-[#A1A1A6]">{t("kasbon.saldoAkhir")}</span>
                        Rp {rp(data.balance)}
                      </td>
                      <td className="px-2 py-3" />
                    </tr>
                  </tfoot>
                </table>
              </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(v) => !v && setDelTarget(null)}
        title={t("kasbon.delTitle")}
        description={delTarget ? t("kasbon.delDesc", { ket: delTarget.keterangan, amt: rp(delTarget.nominal) }) : ""}
        onConfirm={() => { if (delTarget) del(delTarget.id); setDelTarget(null); }}
      />

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="kasbon-edit-dialog" className={`${GLASS} max-w-md`}>
          <DialogHeader className="relative text-left">
            <DialogTitle className="flex items-center gap-2 text-[17px] font-semibold tracking-tight">
              <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#EAF2FE] text-[#0071E3]"><Pencil className="h-4 w-4" /></span>
              {t("kasbon.editTitle")}
            </DialogTitle>
          </DialogHeader>
          {edit && (
          <div className="relative mt-2 space-y-3">
            <div>
              <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.tanggalLbl")}</label>
              <input type="date" data-testid="kasbon-edit-date" value={edit.tanggal} onChange={(e) => setEdit({ ...edit, tanggal: e.target.value })} className={DATE_FIELD} />
            </div>
            {edit.kind === "payment" && (
              <div className="grid grid-cols-2 gap-2.5">
                <Select value={String(edit.bulan)} onValueChange={(v) => setEdit({ ...edit, bulan: +v })}>
                  <SelectTrigger data-testid="kasbon-edit-month" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[14px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-xl">{months.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(edit.tahun)} onValueChange={(v) => setEdit({ ...edit, tahun: +v })}>
                  <SelectTrigger data-testid="kasbon-edit-year" className="h-11 rounded-xl border-[#E1E1E6] bg-white text-[14px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-xl">{[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.colDesc")}</label>
              <input data-testid="kasbon-edit-ket" value={edit.keterangan} onChange={(e) => setEdit({ ...edit, keterangan: e.target.value })} placeholder={t("kasbon.ketPlaceholder")} className={FIELD} />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{t("kasbon.jenis")}</label>
              <CatToggle value={edit.kategori} onPick={(c) => setEdit({ ...edit, kategori: c })} t={t} />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-[#8E8E93]">{edit.kind === "charge" ? t("kasbon.colCharge") : t("kasbon.colPayment")}</label>
              <NumField testid="kasbon-edit-nominal" value={edit.nominal} onChange={(v) => setEdit({ ...edit, nominal: v })} align="text-left" className="h-11 w-full text-[15px]" />
            </div>
            <button data-testid="kasbon-edit-submit" onClick={saveEdit} disabled={busy}
              className="mt-1 flex h-12 w-full items-center justify-center gap-1.5 rounded-xl bg-[#0071E3] text-[15px] font-semibold text-white transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("kasbon.editSave")}
            </button>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
